import React, { createContext, useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import PropTypes from 'prop-types';
import Global from '../../../../../store/global-selectors';
import { LogService } from '../../../../../services/log-service';
import { emptyArray } from 'ui-components/default-props/empty';
import { getGoogleConnections, setGoogleConnections } from '../../../api/integrations-api';

const GoogleContext = createContext({});

const GoogleContextProvider = ({ children }) => {
  const [googleConnectedStoreGuids, setGoogleConnectedStoreGuids] = useAsyncSafeState(emptyArray);

  useEffect(() => {
    getGoogleConnections()
      .then((response) => {
        const guids = response?.data.storeGuids;
        setGoogleConnectedStoreGuids(guids);
      })
      .catch((e) => {
        LogService.logError(e);
        setGoogleConnectedStoreGuids(emptyArray);
      });
  }, [setGoogleConnectedStoreGuids]);

  const allStores = useSelector(Global.selectSellerActiveStores);
  const isGoogleConnected =
    useSelector((state) =>
      Global.selectSellerSellerSetting(state, {
        settingName: 'GoogleTrackingSignalsLinked',
      }),
    ).toLowerCase() === 'true';

  const onSave = useCallback(async () => {
    const payload = { storeGuids: googleConnectedStoreGuids };
    await setGoogleConnections(payload);
  }, [googleConnectedStoreGuids]);

  const value = useMemo(
    () => ({
      allStores,
      googleConnectedStoreGuids,
      isGoogleConnected,
      onSave,
      setGoogleConnectedStoreGuids,
    }),
    [allStores, googleConnectedStoreGuids, isGoogleConnected, onSave, setGoogleConnectedStoreGuids],
  );

  return <GoogleContext.Provider value={value}>{children}</GoogleContext.Provider>;
};

GoogleContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export { GoogleContext };

export default GoogleContextProvider;
